import { computed, ref } from 'vue'
import { api, type DeadLetterEnvelope, type DeadLetterEnvelopeGetRequest, type DeadLetterEnvelopeResults } from '@/api/eOrdersApi'
import { useApiAdapter } from '@/store'

type LoadingStatus = 'idle' | 'pending' | 'success' | 'error'

export interface DeadLetterFilters {
  messageType?: string
  exceptionType?: string
  exceptionMessage?: string
  from?: string
  until?: string
}

export const useDeadLettersData = () => {
  const apiAdapter = useApiAdapter()
  
  const rawData = ref<DeadLetterEnvelopeResults | null>(null)
  const status = ref<LoadingStatus>('idle')
  const error = ref<string | null>(null)
  const currentPage = ref(0)
  const pageSize = ref(20)
  const currentFilters = ref<DeadLetterFilters>({})
  
  const data = computed<DeadLetterEnvelope[]>(() => rawData.value?.envelopes ?? [])
  
  const pagination = computed(() => {
    if (!rawData.value) return null
    
    const totalCount = rawData.value.totalCount ?? 0
    
    return {
      pageSize: pageSize.value,
      currentPage: currentPage.value + 1,
      totalItems: totalCount,
      totalPages: Math.ceil(totalCount / pageSize.value) || 1
    }
  })
  
  const buildRequest = (size: number, page: number, filters: DeadLetterFilters): DeadLetterEnvelopeGetRequest => ({
    limit: size,
    pageNumber: page,
    messageType: filters.messageType || undefined,
    exceptionType: filters.exceptionType || undefined,
    exceptionMessage: filters.exceptionMessage || undefined,
    from: filters.from || undefined,
    until: filters.until || undefined,
  })
  
  const fetchDeadLetters = async (
    size: number = pageSize.value,
    page: number = currentPage.value,
    filters: DeadLetterFilters = currentFilters.value,
    suppressStatusChange = false
  ) => {
    try {
      if (!suppressStatusChange) {
        status.value = 'pending'
      }
      
      error.value = null
      
      const response = await apiAdapter.invoke(config =>
        api.deadLettersAll(buildRequest(size, page, filters), config)
      )
      
      if (!response) {
        throw new Error('Failed to load dead letters.')
      }
      
      const result = Array.isArray(response) ? response[0] : response as DeadLetterEnvelopeResults
      
      rawData.value = result
      status.value = 'success'
      currentPage.value = page
      pageSize.value = size
      currentFilters.value = filters
    } catch (err) {
      console.error('Failed to fetch dead letters', err)
      status.value = 'error'
      error.value = err instanceof Error ? err.message : 'Unknown error'
      rawData.value = null
    }
  }
  
  const applyFilters = async (filters: DeadLetterFilters) => {
    currentFilters.value = filters
    currentPage.value = 0
    
    await fetchDeadLetters(pageSize.value, 0, filters, true)
  }
  
  const goToPage = async (page: number) => {
    const apiPage = page - 1
    if (apiPage < 0) return
    
    await fetchDeadLetters(pageSize.value, apiPage, currentFilters.value)
  }

  const changePageSize = async (newSize: number) => {
    if (newSize === pageSize.value) return
    
    await fetchDeadLetters(newSize, 0, currentFilters.value)
  }
  
  const replay = async (ids: string[]) => {
    const result = await apiAdapter.invokeRaw(
      config => api.replay({ ids }, config) as any
    )

    return result.type === 'success'
  }

  const remove = async (ids: string[]) => {
    const result = await apiAdapter.invokeRaw(
      config => api.deadLetters({ ids }, config) as any
    )

    return result.type === 'success'
  }
  
  return {
    data,
    status,
    error,
    pagination,
    currentFilters: computed(() => currentFilters.value),
    fetchDeadLetters,
    applyFilters,
    goToPage,
    changePageSize,
    replay,
    remove,
  }
}
